import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useCallback } from "react";
import { useSnackbar } from "notistack";
import { Accordion } from "../../../../components/Accordion";
import { mainTheme } from "../../../../theme/mainTheme";
import { getLocaleStringdDate } from "../../../../utils/dateFormatt";
import { CustomCopyClipboard } from "../../../ui/components/CustomCopyToClipboard";
import { useAcknowledgeScreeningEvent } from "../../hooks/useAcknowledgeScreeningEvent";
import { TExternalProviderID, TScreeningEvent } from "../../models";
import { LabelText } from "../DatabaseScreeningPanel/styles";

const ExternalProviderIDRow: FC<{
  externalId: TExternalProviderID;
}> = ({ externalId }) => {
  const providerLabel =
    externalId.provider.toLowerCase() === "sumsub"
      ? "SumSub"
      : externalId.provider;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems="flex-start"
      spacing={1}
      mt={2}
    >
      <LabelText>
        {providerLabel} {externalId.kind} ID:
      </LabelText>
      <Box
        sx={{
          width: "100%",
          flex: "9 1 0",
          display: "flex",
          alignItems: "center",
          fontWeight: 700,
          minWidth: 0,
          "& > span": { minWidth: 0, overflowWrap: "anywhere" },
        }}
      >
        <span>{externalId.value}</span>
        <CustomCopyClipboard text={externalId.value} />
      </Box>
    </Stack>
  );
};

export const ChecksPanel: FC<{
  verificationRequestId: string;
  externalIds: TExternalProviderID[] | null;
  screeningEvents: TScreeningEvent[] | null;
  refreshScreeningEvents: () => Promise<void>;
  isMobile?: boolean;
  onRefresh?: () => Promise<void> | void;
}> = ({
  verificationRequestId,
  externalIds,
  screeningEvents,
  refreshScreeningEvents,
  isMobile = false,
  onRefresh,
}) => {
  const [, acknowledging, , acknowledgeEvent] =
    useAcknowledgeScreeningEvent();
  const { enqueueSnackbar } = useSnackbar();

  const handleIgnoreEvent = useCallback(
    async (eventId: string) => {
      if (!verificationRequestId || acknowledging) {
        return;
      }

      try {
        await acknowledgeEvent(eventId, verificationRequestId);
        enqueueSnackbar("Event ignored", { variant: "success" });
        await refreshScreeningEvents();
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unable to ignore event";
        enqueueSnackbar(message, { variant: "error" });
      }
    },
    [
      acknowledgeEvent,
      acknowledging,
      enqueueSnackbar,
      onRefresh,
      refreshScreeningEvents,
      verificationRequestId,
    ],
  );

  const displayedExternalIds = (externalIds ?? []).filter(
    ({ kind, value }) =>
      (kind === "applicant" || kind === "verification") && value !== "",
  );

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "800",
            fontSize: "20px",
            lineHeight: "28px",
            letterSpacing: "0.5px",
            color: "#1C1F21",
            textTransform: "capitalize",
          }}
        >
          Checks
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ marginTop: "8px" }}>
        {displayedExternalIds.map((externalId, index) => (
          <ExternalProviderIDRow
            key={`${externalId.provider}-${externalId.kind}-${externalId.value}-${index}`}
            externalId={externalId}
          />
        ))}
        {screeningEvents && screeningEvents.length > 0 && (
          <Box mt={2}>
            <LabelText>Screening Events</LabelText>
            {screeningEvents.map((event: TScreeningEvent) => (
              <Box
                key={event.id}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${mainTheme.palette.divider}`,
                  backgroundColor: event.ackedBy
                    ? mainTheme.palette.action.hover
                    : mainTheme.palette.background.paper,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                >
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {event.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getLocaleStringdDate(event.createdAt)}
                    </Typography>
                  </Box>
                  {!event.ackedBy && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleIgnoreEvent(event.id.toString())}
                      disabled={acknowledging}
                    >
                      Ignore
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
